import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import MDButton from "components/MDButton";

/*
  QuizTab
  MCQ runner — mirrors the mobile Quiz tab (select-answer → reveal
  correct/incorrect → tap next → score recap at the end).

  The backend has shipped a few question shapes over time, and the
  legacy quiz infra uses a totally different one. We accept all of
  them so the same component works regardless of where the data
  came from:

    Copilot study-pack (current):
      { id, prompt, choices: [string], correctIndex }

    Legacy MCQ format A:
      { id, question, options: [{ text, isCorrect }] }

    Legacy MCQ format B:
      { id, question, options: [string], correctAnswerIndex }

    Legacy MCQ format C:
      { id, question_content, answers: [{ text, is_correct }] }
*/

function normaliseQuestion(q, idx) {
  let options = [];

  // 1) Modern Copilot study pack: `choices` (string[]) + `correctIndex`.
  //    This is what `generateStudyPack` emits, so it's also the most
  //    common shape — check it first.
  if (Array.isArray(q.choices) && q.choices.length) {
    const correctIdx = Number.isInteger(q.correctIndex)
      ? q.correctIndex
      : Number.isInteger(q.correctAnswerIndex)
        ? q.correctAnswerIndex
        : -1;
    options = q.choices.map((c, i) => {
      // `choices` is usually a plain string array, but the AI very
      // occasionally emits objects — accept both without losing data.
      if (typeof c === "string") {
        return { text: c, isCorrect: i === correctIdx };
      }
      return {
        text: c?.text || c?.label || c?.choice || "",
        isCorrect:
          c?.isCorrect ?? c?.is_correct ?? c?.correct ?? i === correctIdx,
      };
    });
  } else if (Array.isArray(q.options) && q.options.length) {
    // 2) Older `options` shape (objects OR strings + correctAnswerIndex).
    if (typeof q.options[0] === "string") {
      const correctIdx =
        typeof q.correctAnswerIndex === "number"
          ? q.correctAnswerIndex
          : typeof q.correctIndex === "number"
            ? q.correctIndex
            : -1;
      options = q.options.map((text, i) => ({
        text,
        isCorrect: i === correctIdx,
      }));
    } else {
      options = q.options.map((o) => ({
        text: o.text || o.label || "",
        isCorrect: !!(o.isCorrect ?? o.is_correct),
      }));
    }
  } else if (Array.isArray(q.answers)) {
    // 3) Legacy quiz-question shape from the original Microlab quiz
    //    table — kept around so the same tab can run AI-generated
    //    quizzes and human-authored ones.
    options = q.answers.map((a) => ({
      text: a.answer_text || a.text || "",
      isCorrect: !!(a.is_correct ?? a.isCorrect),
    }));
  }

  return {
    id: q.id || `q-${idx}`,
    question:
      q.prompt ||
      q.question ||
      q.question_content ||
      q.text ||
      `Question ${idx + 1}`,
    options,
    explanation: q.explanation || q.rationale || "",
  };
}

export default function QuizTab({ studyPack }) {
  const questions = useMemo(
    () => (Array.isArray(studyPack?.quiz?.questions) ? studyPack.quiz.questions.map(normaliseQuestion) : []),
    [studyPack],
  );

  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState({}); // { [qIdx]: optionIdx }
  const [revealed, setRevealed] = useState({}); // { [qIdx]: true }
  const [done, setDone] = useState(false);

  // Reset state whenever a fresh study pack arrives (e.g. user
  // hit "More quizzes" from Copilot Chat and the questions list
  // grew).
  useEffect(() => {
    setIdx(0);
    setSelected({});
    setRevealed({});
    setDone(false);
  }, [studyPack?.quiz?.questions?.length, studyPack?.updatedAt]);

  if (!questions.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
        <Typography variant="body2" color="text.secondary">
          No quiz questions yet. Ask Copilot for "more quizzes" in the Chat tab to generate some.
        </Typography>
      </Paper>
    );
  }

  if (done) {
    const correct = questions.reduce((acc, q, i) => {
      const sel = selected[i];
      if (typeof sel !== "number") return acc;
      return q.options[sel]?.isCorrect ? acc + 1 : acc;
    }, 0);
    const pct = Math.round((correct / questions.length) * 100);
    return (
      <Paper
        variant="outlined"
        sx={(t) => ({
          p: 4,
          borderRadius: 3,
          textAlign: "center",
          borderColor: alpha(t.palette.success.main, 0.4),
          backgroundColor: alpha(t.palette.success.main, 0.04),
        })}
      >
        <EmojiEventsRoundedIcon sx={{ fontSize: 56, color: "warning.main" }} />
        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
          {correct} / {questions.length} correct
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          That's {pct}% — keep practising to lock it in.
        </Typography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<ReplayRoundedIcon />}
          onClick={() => {
            setIdx(0);
            setSelected({});
            setRevealed({});
            setDone(false);
          }}
        >
          Try again
        </MDButton>
      </Paper>
    );
  }

  const q = questions[idx];
  const isRevealed = !!revealed[idx];
  const userPick = selected[idx];
  const isLast = idx === questions.length - 1;

  const onPick = (i) => {
    if (isRevealed) return;
    setSelected((prev) => ({ ...prev, [idx]: i }));
  };

  const onCheck = () => {
    if (typeof userPick !== "number") return;
    setRevealed((prev) => ({ ...prev, [idx]: true }));
  };

  const onNext = () => {
    if (isLast) {
      setDone(true);
    } else {
      setIdx(idx + 1);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Question {idx + 1} / {questions.length}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${Math.round(((idx + (isRevealed ? 1 : 0)) / questions.length) * 100)}% progress`}
        />
      </Stack>
      <Paper
        variant="outlined"
        sx={(t) => ({
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          borderColor: t.palette.divider,
        })}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {q.question}
        </Typography>
        <Stack spacing={1.2}>
          {q.options.map((o, i) => {
            const isPicked = userPick === i;
            const correctColor = "success";
            const wrongColor = "error";
            const baseBorderRadius = 2;
            return (
              <Paper
                key={i}
                variant="outlined"
                onClick={() => onPick(i)}
                sx={(t) => ({
                  p: 1.4,
                  borderRadius: baseBorderRadius,
                  cursor: isRevealed ? "default" : "pointer",
                  borderColor:
                    isRevealed && o.isCorrect
                      ? t.palette.success.main
                      : isRevealed && isPicked && !o.isCorrect
                        ? t.palette.error.main
                        : isPicked
                          ? t.palette.primary.main
                          : t.palette.divider,
                  backgroundColor:
                    isRevealed && o.isCorrect
                      ? alpha(t.palette.success.main, 0.06)
                      : isRevealed && isPicked && !o.isCorrect
                        ? alpha(t.palette.error.main, 0.06)
                        : isPicked
                          ? alpha(t.palette.primary.main, 0.06)
                          : "transparent",
                  transition: "all .15s ease",
                  "&:hover": isRevealed
                    ? undefined
                    : { borderColor: t.palette.primary.main },
                })}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={(t) => ({
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: `2px solid ${isPicked ? t.palette.primary.main : t.palette.divider}`,
                      backgroundColor: isPicked ? t.palette.primary.main : "transparent",
                      flexShrink: 0,
                    })}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {o.text}
                  </Typography>
                  {isRevealed && o.isCorrect ? (
                    <CheckCircleRoundedIcon color={correctColor} fontSize="small" />
                  ) : isRevealed && isPicked && !o.isCorrect ? (
                    <CancelRoundedIcon color={wrongColor} fontSize="small" />
                  ) : null}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
        {isRevealed && q.explanation ? (
          <Paper
            variant="outlined"
            sx={(t) => ({
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              borderColor: alpha(t.palette.info.main, 0.4),
              backgroundColor: alpha(t.palette.info.main, 0.04),
            })}
          >
            <Typography variant="caption" fontWeight={700} color="info.main">
              Explanation
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.3 }}>
              {q.explanation}
            </Typography>
          </Paper>
        ) : null}
      </Paper>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {isRevealed ? (
          <MDButton variant="gradient" color="info" onClick={onNext}>
            {isLast ? "See score" : "Next question"}
          </MDButton>
        ) : (
          <MDButton
            variant="gradient"
            color="primary"
            onClick={onCheck}
            disabled={typeof userPick !== "number"}
          >
            Check answer
          </MDButton>
        )}
      </Stack>
    </Stack>
  );
}
